import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Radio, Loader2, Save, PlayCircle, StopCircle, CalendarClock, Trash2, Plus } from "lucide-react";

const EVENT_TYPES = ["CPI", "NFP", "FOMC", "Other"];

export function LiveControl() {
  const [f, setF] = useState({
    event_type: "FOMC", event_title: "", event_time: "", stream_url: "",
    analyst_name: "", previous: "", forecast: "", actual: "", usd_note: "", gold_note: "",
  });
  const [isLive, setIsLive] = useState(false);
  const [busy, setBusy] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [sched, setSched] = useState({ event_type: "NFP", title: "", event_time: "" });

  const loadSchedule = async () => {
    try { const { data } = await api.get("/admin/live/schedule"); setSchedule(data || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadSchedule(); }, []);

  const addSchedule = async () => {
    if (!sched.event_time) { toast.error("Mete dat/lè a"); return; }
    setBusy("sched");
    try {
      await api.post("/admin/live/schedule", { ...sched, event_time: new Date(sched.event_time).toISOString() });
      setSched({ event_type: "NFP", title: "", event_time: "" });
      await loadSchedule();
      toast.success("Live pwograme");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const delSchedule = async (id) => {
    try { await api.delete(`/admin/live/schedule/${id}`); await loadSchedule(); } catch { /* ignore */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/live/session");
      if (data?.exists) {
        setIsLive(!!data.is_live);
        setF((p) => ({
          ...p,
          event_type: data.event_type || "FOMC", event_title: data.event_title || "",
          event_time: (data.event_time || "").slice(0, 16), stream_url: data.stream_url || "",
          analyst_name: data.analyst_name || "", previous: data.previous || "",
          forecast: data.forecast || "", actual: data.actual || "",
          usd_note: data.usd_note || "", gold_note: data.gold_note || "",
        }));
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target?.value ?? e }));

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...f, event_time: f.event_time ? new Date(f.event_time).toISOString() : null };
      await api.put("/admin/live/session", payload);
      toast.success("Sesyon live la anrejistre");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const goLive = async () => {
    setBusy("live");
    try {
      await save();
      await api.post("/admin/live/go-live");
      setIsLive(true);
      toast.success("Ou an dirèk! Notifikasyon voye bay kliyan yo.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const endLive = async () => {
    setBusy("end");
    try { await api.post("/admin/live/end"); setIsLive(false); toast.success("Live la fini"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="border border-[#222] bg-[#0F0F0F] p-5 rounded-sm space-y-4" data-testid="admin-live-control">
      <div className="flex items-center gap-2">
        <Radio className="h-5 w-5 text-[#ff5b5b]" />
        <span className="font-heading font-semibold">Sal Analis an Dirèk</span>
        {isLive && <span className="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-[#ff2d2d]/15 text-[#ff5b5b] text-[11px] font-bold font-mono-term uppercase tracking-widest"><span className="h-2 w-2 rounded-full bg-[#ff2d2d] live-dot" /> LIVE</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#888]">Tip Evènman</Label>
          <Select value={f.event_type} onValueChange={(v) => setF((p) => ({ ...p, event_type: v }))}>
            <SelectTrigger data-testid="live-event-type" className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-[#222] text-white">
              {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-[#888]">Tit Evènman</Label>
          <Input data-testid="live-title" value={f.event_title} onChange={set("event_title")} placeholder="FOMC Rate Decision" className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" />
        </div>
        <div>
          <Label className="text-xs text-[#888]">Lè Evènman (countdown)</Label>
          <Input data-testid="live-time" type="datetime-local" value={f.event_time} onChange={set("event_time")} className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" />
        </div>
        <div>
          <Label className="text-xs text-[#888]">Non Analis la</Label>
          <Input data-testid="live-analyst" value={f.analyst_name} onChange={set("analyst_name")} placeholder="TrzeFx Analyst" className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" />
        </div>
      </div>

      <div>
        <Label className="text-xs text-[#888]">Lyen YouTube Live la</Label>
        <Input data-testid="live-stream-url" value={f.stream_url} onChange={set("stream_url")} placeholder="https://www.youtube.com/watch?v=..." className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" />
        <p className="text-[10px] text-[#666] mt-1">Kole lyen YouTube Live la (watch, youtu.be, oswa /live/). App la ap entegre l otomatikman.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-xs text-[#888]">Previous</Label><Input data-testid="live-previous" value={f.previous} onChange={set("previous")} placeholder="3.2%" className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" /></div>
        <div><Label className="text-xs text-[#888]">Forecast</Label><Input data-testid="live-forecast" value={f.forecast} onChange={set("forecast")} placeholder="3.1%" className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" /></div>
        <div><Label className="text-xs text-[#888]">Actual</Label><Input data-testid="live-actual" value={f.actual} onChange={set("actual")} placeholder="—" className="mt-1.5 h-11 rounded-sm bg-[#0A0A0A] border-[#222]" /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs text-[#888]">Nòt sou USD</Label><Textarea data-testid="live-usd-note" value={f.usd_note} onChange={set("usd_note")} rows={2} placeholder="Fòs dola a..." className="mt-1.5 rounded-sm bg-[#0A0A0A] border-[#222]" /></div>
        <div><Label className="text-xs text-[#888]">Nòt sou Gold (XAUUSD)</Label><Textarea data-testid="live-gold-note" value={f.gold_note} onChange={set("gold_note")} rows={2} placeholder="Reyaksyon lò a..." className="mt-1.5 rounded-sm bg-[#0A0A0A] border-[#222]" /></div>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={save} disabled={busy === "save"} data-testid="live-save" className="h-11 rounded-sm bg-[#1C8CFF] hover:bg-[#0f7ae6] text-white font-semibold">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" />Anrejistre</>}
        </Button>
        {!isLive ? (
          <Button onClick={goLive} disabled={busy === "live"} data-testid="live-go" className="h-11 rounded-sm bg-[#ff2d2d] hover:bg-[#e02020] text-white font-semibold">
            {busy === "live" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-1.5" />Ale an Dirèk (Go Live)</>}
          </Button>
        ) : (
          <Button onClick={endLive} disabled={busy === "end"} data-testid="live-end" className="h-11 rounded-sm bg-[#222] hover:bg-[#333] text-white font-semibold">
            {busy === "end" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><StopCircle className="h-4 w-4 mr-1.5" />Fini Live la</>}
          </Button>
        )}
      </div>

      {/* Kalandriye — schedule upcoming lives */}
      <div className="border-t border-[#222] pt-4 space-y-3" data-testid="live-schedule-admin">
        <div className="flex items-center gap-2 text-sm font-semibold"><CalendarClock className="h-4 w-4 text-[#1C8CFF]" /> Kalandriye Live (pwochen live yo)</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select value={sched.event_type} onValueChange={(v) => setSched((p) => ({ ...p, event_type: v }))}>
            <SelectTrigger data-testid="sched-type" className="h-11 rounded-sm bg-[#0A0A0A] border-[#222]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-[#222] text-white">
              {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="sched-title" value={sched.title} onChange={(e) => setSched((p) => ({ ...p, title: e.target.value }))} placeholder="Tit" className="h-11 rounded-sm bg-[#0A0A0A] border-[#222]" />
          <Input data-testid="sched-time" type="datetime-local" value={sched.event_time} onChange={(e) => setSched((p) => ({ ...p, event_time: e.target.value }))} className="h-11 rounded-sm bg-[#0A0A0A] border-[#222]" />
          <Button onClick={addSchedule} disabled={busy === "sched"} data-testid="sched-add" className="h-11 rounded-sm bg-[#1C8CFF] hover:bg-[#0f7ae6] text-white font-semibold">
            {busy === "sched" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Ajoute</>}
          </Button>
        </div>
        {schedule.length > 0 && (
          <div className="space-y-1.5">
            {schedule.map((s) => (
              <div key={s.id} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#222] rounded-sm px-3 py-2">
                <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold font-mono-term bg-[#1C8CFF]/15 text-[#1C8CFF]">{s.event_type}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">{s.title}</div>
                  <div className="font-mono-term text-[10px] text-[#666]">{new Date(s.event_time).toLocaleString()}</div>
                </div>
                <button onClick={() => delSchedule(s.id)} data-testid={`sched-del-${s.id}`} className="text-[#ff5b5b] hover:text-[#ff8080]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
